import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  AlertTriangle,
  Activity,
  Clock,
  UserPlus,
  User,
  Users,
  CheckCircle,
  MessageSquare,
  AtSign,
  Info,
  ClipboardList,
  Calendar,
  Megaphone,
  CheckCircle2,
  Filter,
  Sparkles,
  FileText,
} from "lucide-react";
import {
  Box,
  Typography,
  Skeleton,
  Tooltip,
  IconButton,
  TablePagination,
} from "@mui/material";
import { EditNote } from "@mui/icons-material";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/Card";
import { Button } from "../../../components/Button";
import { EmptyStateMessage } from "../../../components/EmptyStateMessage";
import { FilterSwitchGroup } from "../../../components/FilterSwitchGroup";
import { UnifiedModal } from "../../../components/UnifiedModal";
import { useTheme } from "../../../theme/useTheme";
import { formatRelativeDate } from "../../../utils/dateUtils";
import apiClient from "../../../api-client";
import { useQueryClient } from "@tanstack/react-query";

// === NOTIFICATION TYPE CONFIGURATION ===
export const NOTIFICATION_TYPES = {
  // Tickets
  ticket_created: {
    label: "Ticket creado",
    icon: AlertTriangle,
    category: "ticket",
    description: "New ticket",
    receivers: ["SuperAdmin"],
  },
  ticket_assigned: {
    label: "Ticket asignado",
    icon: AlertTriangle,
    category: "ticket",
    description: "Ticket assigned",
    receivers: ["SuperAdmin", "Ticket freelancer"],
  },
  ticket_status_changed: {
    label: "Estado de ticket",
    icon: Activity,
    category: "ticket",
    description: "Status change",
    receivers: ["Agency users", "Ticket freelancer"],
  },
  ticket_hours_redeemed: {
    label: "Tiempo de ticket canjeado",
    icon: Clock,
    category: "ticket",
    description: "Hours redeemed",
    receivers: ["Agency Admin"],
  },

  // Users
  user_created: {
    label: "Nuevo usuario",
    icon: UserPlus,
    category: "user",
    description: "New user",
    receivers: ["SuperAdmin", "Agency Admin"],
  },
  client_info_updated: {
    label: "Cliente actualizado",
    icon: User,
    category: "user",
    description: "Client data changed",
    receivers: ["Agency users"],
  },
  client_created: {
    label: "Cliente creado",
    icon: Users,
    category: "user",
    description: "Client created",
    receivers: ["SuperAdmin"],
  },
  client_onboarding_completed: {
    label: "Onboarding completado",
    icon: CheckCircle,
    category: "user",
    description: "Onboarding done",
    receivers: ["Agency users"],
  },

  // Conversations
  unread_in_thread: {
    label: "Mensajes sin leer",
    icon: MessageSquare,
    category: "conversation",
    description: "Unread messages counter",
    receivers: ["All roles"],
  },
  conversation_mention: {
    label: "Te han mencionado",
    icon: AtSign,
    category: "conversation",
    description: "User mentioned",
    receivers: ["All roles"],
  },
  conversation_important: {
    label: "Mensaje importante",
    icon: Info,
    category: "conversation",
    description: "Important message",
    receivers: ["SuperAdmin", "Agency users", "Client users"],
  },
  conversation_task_assigned: {
    label: "Tarea de conversación",
    icon: ClipboardList,
    category: "conversation",
    description: "Task from message",
    receivers: ["SuperAdmin", "Agency users", "Client users"],
  },

  // Tasks
  task_assigned: {
    label: "Tarea asignada",
    icon: ClipboardList,
    category: "task",
    description: "Task assigned",
    receivers: ["All roles"],
  },
  task_deadline: {
    label: "Plazo de tarea",
    icon: Calendar,
    category: "task",
    description: "Deadline reminder",
    receivers: ["All roles"],
  },

  // Other
  central_announcement: {
    label: "Anuncio del sistema",
    icon: Megaphone,
    category: "other",
    description: "System announcement",
    receivers: ["Agency users enabled for announcements"],
  },
  project_phase_completed: {
    label: "Fase completada",
    icon: CheckCircle2,
    category: "other",
    description: "Phase finished",
    receivers: ["Client users"],
  },

  // Content generation
  content_review: {
    label: "Creación de contenido",
    icon: Sparkles,
    category: "content",
    description: "Content ready for review",
    receivers: ["Client users", "Agency users"],
  },
  content_updated: {
    label: "Creación de contenido",
    icon: Sparkles,
    category: "content",
    description: "Content updated",
    receivers: ["Client users", "Agency users"],
  },
  // Reports
  report_available: {
    label: "Informe disponible",
    icon: FileText,
    category: "report",
    description: "Monthly report available",
    receivers: ["Client users"],
  },
};

// Helper to get notification category
export const getNotificationCategory = (notificationType) => {
  return NOTIFICATION_TYPES[notificationType]?.category || "other";
};

// Helper to format message content with mentions
const formatMessageContent = (content) => {
  if (!content) return content;

  // Replace mention format @[Name](user:id) with @Name
  const mentionRegex = /@\[([^\]]+)\]\(user:\d+\)/g;
  return content.replace(mentionRegex, (match, name) => {
    // Keep original case and remove extra spaces
    return `@${name.trim()}`;
  });
};

// Filter configuration: only read/unread filters, matching Incidents styling
export const NOTIFICATION_STATUS_FILTERS = {
  unread: { label: "No leídas", isRead: false },
  read: { label: "Leídas", isRead: true },
};

/**
 * NotificationPanel Component
 *
 * A reusable notification panel that displays grouped notifications with filtering,
 * mark as read functionality, and navigation to related content.
 *
 * @param {Object} props
 * @param {Array} props.notifications - Array of notification objects
 * @param {boolean} props.isLoading - Loading state for notifications
 * @param {Error} props.error - Error object if loading failed
 * @param {number} props.unreadCount - Count of unread notifications
 * @param {boolean} props.isUnreadCountLoading - Loading state for unread count
 * @param {Function} props.onMarkAllRead - Callback to mark all notifications as read
 * @param {Function} props.onMarkRead - Callback to mark single notification as read
 * @param {Function} props.onNotificationClick - Callback when notification is clicked
 * @param {boolean} props.isMarkingAllRead - State indicating if marking all as read is in progress
 * @param {string|null} props.clearingThreadId - Thread ID currently being cleared
 * @param {Object} props.currentUser - Current user object
 * @param {Object} props.clientNameById - Map of client IDs to names
 * @param {Function} props.getNotificationSummary - Function to get notification summary text
 * @param {Function} props.getNotificationRoute - Function to get notification route
 * @param {string} props.selectedNotificationFilter - Current filter ('read' or 'unread')
 * @param {boolean} props.hasNotifications - Whether there are any notifications
 * @param {Object} props.sx - Additional MUI sx props for the Card container
 * @param {boolean} props.showFilters - Whether to show filter switch group (for admin/superadmin views)
 * @param {Function} props.onFilterChange - Callback when filter is changed
 * @param {React.ReactNode} props.filterRightContent - Optional right content for filter area (e.g., advanced filter button)
 * @param {boolean} props.showPagination - Whether to show pagination
 * @param {number} props.page - Current page number (0-indexed)
 * @param {number} props.rowsPerPage - Number of rows per page
 * @param {number} props.totalCount - Total count of items for pagination
 * @param {Function} props.onPageChange - Callback when page changes
 * @param {Function} props.onRowsPerPageChange - Callback when rows per page changes
 */
export function NotificationPanel({
  notifications = [],
  isLoading = false,
  error = null,
  unreadCount = 0,
  isUnreadCountLoading = false,
  onMarkAllRead,
  onMarkRead,
  onNotificationClick,
  isMarkingAllRead = false,
  clearingThreadId = null,
  currentUser = null,
  clientNameById = {},
  getNotificationSummary,
  getNotificationRoute,
  selectedNotificationFilter = "unread",
  hasNotifications = false,
  sx = {},
  // Enhanced features for admin/superadmin views
  showFilters = false,
  onFilterChange = null,
  filterRightContent = null,
  showPagination = false,
  page = 0,
  rowsPerPage = 25,
  totalCount = 0,
  onPageChange = null,
  onRowsPerPageChange = null,
}) {
  const { theme, colors, neutrals, notificationColors } = useTheme();
  const [isLegendOpen, setIsLegendOpen] = React.useState(false);

  const notificationLegendItems = React.useMemo(() => {
    const seen = new Set();
    const isClientUser = currentUser?.role === "client";
    const userRole = currentUser?.role;
    const roleMatchesReceivers = (receivers = []) => {
      if (!receivers.length) return true;
      if (receivers.includes("All roles")) return true;

      if (userRole === "superadmin") {
        return receivers.includes("SuperAdmin");
      }

      if (userRole === "agency_admin") {
        return (
          receivers.includes("Agency Admin") || receivers.includes("Agency users")
        );
      }

      if (userRole === "consultant") {
        return receivers.includes("Agency users");
      }

      if (userRole === "client") {
        return receivers.includes("Client users");
      }

      if (userRole === "freelance_ticket") {
        return receivers.includes("Ticket freelancer");
      }

      return false;
    };

    return Object.entries(NOTIFICATION_TYPES).reduce(
      (acc, [type, config]) => {
        if (type === "task_deadline") return acc;
        if (isClientUser && type === "task_assigned") return acc;
        if (isClientUser && type === "conversation_task_assigned") return acc;
        if (type === "ticket_assigned" && userRole !== "superadmin") {
          return acc;
        }
        if (type === "client_created" && userRole !== "superadmin") {
          return acc;
        }

        const receivers = config.receivers || [];
        if (!roleMatchesReceivers(receivers)) return acc;

        const legendLabel =
          type === "conversation_task_assigned"
            ? "Tarea asignada"
            : userRole === "superadmin" &&
              (type === "ticket_created" || type === "ticket_assigned")
            ? "Ticket creado / Ticket asignado"
            : config.label;
        const legendIcon =
          type === "content_review" || type === "content_updated"
            ? EditNote
            : config.icon;
        const iconName = legendIcon?.name || "icon";
        const key = `${legendLabel}-${iconName}`;
        if (seen.has(key)) return acc;
        seen.add(key);
        acc.push({ type, label: legendLabel, icon: legendIcon });
        return acc;
      },
      []
    );
  }, [currentUser?.role]);

  const defaultNotificationColor = React.useMemo(
    () => ({
      text: notificationColors?.default?.text || neutrals.gray[600],
      dot: notificationColors?.default?.dot || neutrals.gray[400],
      background: notificationColors?.default?.background || neutrals.gray[100],
    }),
    [notificationColors, neutrals]
  );

  const getNotificationColorConfig = React.useCallback(
    (type) => {
      if (type && notificationColors?.[type]) {
        return notificationColors[type];
      }
      return defaultNotificationColor;
    },
    [notificationColors, defaultNotificationColor]
  );

  const shouldDisplayClientName = (notification) => {
    if (!notification) return false;
    if (notification.thread_type === "ticket" || notification.ticket_id) {
      return true;
    }
    return getNotificationCategory(notification.type) === "conversation";
  };

  const getClientNameToDisplay = React.useCallback(
    (notification) => {
      if (!notification || !shouldDisplayClientName(notification)) return null;

      const clientId =
        notification.client_id ||
        notification.data?.client_id ||
        notification.client?.id ||
        notification.data?.client?.id;

      if (!clientId) return null;

      return clientNameById[clientId] || null;
    },
    [clientNameById]
  );

  const EllipsisTooltip = ({
    text,
    placement = "bottom",
    typographySx = {},
    ...typographyProps
  }) => {
    const textRef = React.useRef(null);
    const [isOverflowed, setIsOverflowed] = React.useState(false);

    React.useEffect(() => {
      const el = textRef.current;
      if (!el) return undefined;

      const checkOverflow = () => {
        const hasOverflow =
          el.scrollHeight > el.clientHeight + 1 ||
          el.scrollWidth > el.clientWidth + 1;
        setIsOverflowed(hasOverflow);
      };

      checkOverflow();

      let resizeObserver;
      if (typeof ResizeObserver !== "undefined") {
        resizeObserver = new ResizeObserver(checkOverflow);
        resizeObserver.observe(el);
      }

      window.addEventListener("resize", checkOverflow);
      return () => {
        window.removeEventListener("resize", checkOverflow);
        if (resizeObserver) resizeObserver.disconnect();
      };
    }, [text]);

    const content = (
      <Typography ref={textRef} sx={typographySx} {...typographyProps}>
        {text}
      </Typography>
    );

    if (!text) return content;

    return isOverflowed ? (
      <Tooltip title={text} arrow placement={placement}>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  return (
    <>
      <Card
        sx={{
          maxHeight: { xs: "none", lg: "calc(100vh - 400px)" },
          display: "flex",
          flexDirection: "column",
          ...sx,
        }}
      >
        <CardHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: showFilters ? 1.5 : 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: showFilters ? 2 : 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    position: "relative",
                    width: 24,
                    height: 24,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Bell size={20} />
                  {!isUnreadCountLoading && unreadCount > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: -2,
                        right: -2,
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        backgroundColor: colors.error.main,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.error.contrastText,
                          fontSize: "0.65rem",
                          fontWeight: 600,
                          lineHeight: 1,
                        }}
                      >
                        {unreadCount}
                      </Typography>
                    </Box>
                  )}
                </Box>
                <Typography
                  variant={showFilters ? "h6" : "inherit"}
                  sx={showFilters ? { fontWeight: 600 } : {}}
                >
                  {showFilters ? (
                    "Notificaciones"
                  ) : (
                    <CardTitle>Notificaciones</CardTitle>
                  )}
                </Typography>
              </Box>

              {showFilters && onFilterChange && (
                <FilterSwitchGroup
                  options={Object.entries(NOTIFICATION_STATUS_FILTERS).map(
                    ([key, config]) => ({
                      value: key,
                      label: (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                          }}
                        >
                          {config.label}
                        </Box>
                      ),
                    })
                  )}
                  selectedValue={selectedNotificationFilter}
                  onChange={onFilterChange}
                  rightContent={filterRightContent}
                />
              )}
            </Box>

            <Button
              variant="ghost"
              size="sm"
              onClick={onMarkAllRead}
              disabled={
                isMarkingAllRead ||
                !hasNotifications ||
                selectedNotificationFilter === "read"
              }
            >
              {isMarkingAllRead ? "Marcando..." : "Marcar todo como leído"}
            </Button>
          </Box>
        </CardHeader>
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            maxHeight: "100%",
            overflow: showPagination ? "hidden" : "auto",
            pb: showPagination ? 0 : undefined,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: neutrals.gray[100],
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: neutrals.gray[300],
              borderRadius: "4px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: showPagination ? 2 : 1.5,
              flex: showPagination ? 1 : undefined,
              minHeight: showPagination ? 0 : undefined,
            }}
          >
            {isLoading ? (
              showPagination ? (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 1,
                  }}
                >
                  {Array.from({ length: 6 }).map((_, index) => (
                    <Box
                      key={`notification-skeleton-${index}`}
                      sx={{
                        p: 1.5,
                        border: `1px solid ${neutrals.gray[200]}`,
                        borderRadius: "12px",
                      }}
                    >
                      <Skeleton variant="text" width="90%" height={18} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                  ))}
                </Box>
              ) : (
                Array.from({ length: 3 }).map((_, index) => (
                  <Box
                    key={`notification-skeleton-${index}`}
                    sx={{
                      p: 1.5,
                      border: `1px solid ${neutrals.gray[200]}`,
                      borderRadius: "12px",
                    }}
                  >
                    <Skeleton variant="text" width="90%" height={18} />
                    <Skeleton variant="text" width="60%" height={16} />
                  </Box>
                ))
              )
            ) : (
              <>
                {error && (
                  <Typography variant="body2" color="error">
                    Ocurrió un error al cargar tus notificaciones.
                  </Typography>
                )}
                {hasNotifications ? (
                  showPagination ? (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          flex: 1,
                          minHeight: 0,
                          overflowY: "auto",
                          overflowX: "hidden",
                          pr: 1,
                          "&::-webkit-scrollbar": {
                            width: "6px",
                          },
                          "&::-webkit-scrollbar-track": {
                            backgroundColor: neutrals.gray[100],
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: neutrals.gray[300],
                            borderRadius: "4px",
                          },
                        }}
                      >
                        <Box
                          component="ul"
                          sx={{
                            listStyle: "none",
                            p: 0,
                            m: 0,
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {notifications.map((notification, index) => {
                            const isUnread = !notification.is_read;
                            const isClearingThread =
                              clearingThreadId !== null &&
                              notification.thread_id === clearingThreadId;
                            const destination = getNotificationRoute
                              ? getNotificationRoute(notification)
                              : null;
                            const isCentralAnnouncement =
                              notification.type === "central_announcement";
                            const isTaskAssigned =
                              notification.type === "task_assigned" &&
                              notification.task_id;
                            const isContentReview =
                              notification.type === "content_review" ||
                              notification.type === "content_updated";
                            const isClickable =
                              (isCentralAnnouncement ||
                                isTaskAssigned ||
                                isContentReview ||
                                Boolean(destination)) &&
                              !isClearingThread;
                            const timestamp = (
                              notification.type === "unread_in_thread"
                                ? notification.updated_at
                                : notification.created_at
                            )
                              ? formatRelativeDate(
                                new Date(
                                  notification.type === "unread_in_thread"
                                    ? notification.updated_at
                                    : notification.created_at
                                )
                              )
                              : "";
                            const colorConfig = getNotificationColorConfig(
                              notification.type
                            );
                            const isContentNotification =
                              notification.type === "content_review" ||
                              notification.type === "content_updated";
                            const NotificationIcon = isContentNotification
                              ? EditNote
                              : NOTIFICATION_TYPES[notification.type]?.icon || Bell;
                            const summaryText = getNotificationSummary
                              ? getNotificationSummary(notification)
                              : "Nueva actividad registrada.";

                            return (
                              <Box
                                component="li"
                                key={notification.id}
                                sx={{
                                  display: "flex",
                                  alignItems: {
                                    xs: "flex-start",
                                    sm: "center",
                                  },
                                  gap: 1,
                                  py: 0.75,
                                  borderBottom:
                                    index === notifications.length - 1
                                      ? "none"
                                      : `1px dashed ${neutrals.gray[100]}`,
                                  cursor: isClearingThread
                                    ? "wait"
                                    : isClickable
                                      ? "pointer"
                                      : "default",
                                  opacity: isClearingThread ? 0.6 : 1,
                                  transition: isClickable
                                    ? "background-color 0.2s ease"
                                    : "none",
                                  "&:hover": isClickable
                                    ? {
                                      backgroundColor:
                                        theme.palette.action.hover,
                                    }
                                    : undefined,
                                }}
                                onClick={
                                  isClickable
                                    ? () => onNotificationClick(notification)
                                    : undefined
                                }
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      flexShrink: 0,
                                      marginTop: { xs: "4px", sm: 0 },
                                    }}
                                  >
                                    <Tooltip
                                      title={
                                        NOTIFICATION_TYPES[notification.type]?.label ||
                                        "Notificación"
                                      }
                                      arrow
                                      placement="bottom"
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        {NotificationIcon === EditNote ? (
                                          <NotificationIcon
                                            sx={{
                                              color: colorConfig.text,
                                              fontSize: isContentNotification
                                                ? 18
                                                : 16,
                                            }}
                                          />
                                        ) : (
                                          <NotificationIcon
                                            size={16}
                                            color={colorConfig.text}
                                          />
                                        )}
                                      </Box>
                                    </Tooltip>
                                  </Box>
                                  <EllipsisTooltip
                                    text={summaryText}
                                    placement="bottom"
                                    variant="body2"
                                    typographySx={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontWeight: isUnread ? 600 : 500,
                                      color: theme.palette.text.primary,
                                      display: "-webkit-box",
                                      WebkitLineClamp: 2,
                                      WebkitBoxOrient: "vertical",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                    }}
                                  />
                                </Box>
                                {timestamp && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: theme.palette.text.secondary,
                                    }}
                                  >
                                    {timestamp}
                                  </Typography>
                                )}
                                {isUnread && (
                                  <Tooltip title="Marcar como leído" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        onMarkRead(notification.id);
                                      }}
                                      sx={{
                                        color: colorConfig.text,
                                        "&:hover": {
                                          backgroundColor:
                                            theme.palette.action.hover,
                                        },
                                      }}
                                    >
                                      <CheckCheck size={16} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        overflowY: "auto",
                        overflowX: "hidden",
                        pr: 1,
                        "&::-webkit-scrollbar": {
                          width: "6px",
                        },
                        "&::-webkit-scrollbar-track": {
                          backgroundColor: neutrals.gray[100],
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: neutrals.gray[300],
                          borderRadius: "4px",
                        },
                      }}
                    >
                      <Box
                        component="ul"
                        sx={{
                          listStyle: "none",
                          p: 0,
                          m: 0,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        {notifications.map((notification, index) => {
                          const isUnread = !notification.is_read;
                          const isClearingThread =
                            clearingThreadId !== null &&
                            notification.thread_id === clearingThreadId;
                          const destination = getNotificationRoute
                            ? getNotificationRoute(notification)
                            : null;
                          const isCentralAnnouncement =
                            notification.type === "central_announcement";
                          const isTaskAssigned =
                            notification.type === "task_assigned" &&
                            notification.task_id;
                          const isContentReview =
                            notification.type === "content_review" ||
                            notification.type === "content_updated";
                          const isClickable =
                            (isCentralAnnouncement ||
                              isTaskAssigned ||
                              isContentReview ||
                              Boolean(destination)) &&
                            !isClearingThread;
                          const timestamp = (
                            notification.type === "unread_in_thread"
                              ? notification.updated_at
                              : notification.created_at
                          )
                            ? formatRelativeDate(
                              new Date(
                                notification.type === "unread_in_thread"
                                  ? notification.updated_at
                                  : notification.created_at
                              )
                            )
                            : "";
                          const colorConfig = getNotificationColorConfig(
                            notification.type
                          );
                          const isContentNotification =
                            notification.type === "content_review" ||
                            notification.type === "content_updated";
                          const NotificationIcon = isContentNotification
                            ? EditNote
                            : NOTIFICATION_TYPES[notification.type]?.icon || Bell;
                          const summaryText = getNotificationSummary
                            ? getNotificationSummary(notification)
                            : "Nueva actividad registrada.";

                          return (
                            <Box
                              component="li"
                              key={notification.id}
                              sx={{
                                display: "flex",
                                alignItems: {
                                  xs: "flex-start",
                                  sm: "center",
                                },
                                gap: 1,
                                py: 0.75,
                                borderBottom:
                                  index === notifications.length - 1
                                    ? "none"
                                    : `1px dashed ${neutrals.gray[100]}`,
                                cursor: isClearingThread
                                  ? "wait"
                                  : isClickable
                                    ? "pointer"
                                    : "default",
                                opacity: isClearingThread ? 0.6 : 1,
                                transition: isClickable
                                  ? "background-color 0.2s ease"
                                  : "none",
                                "&:hover": isClickable
                                  ? {
                                    backgroundColor:
                                      theme.palette.action.hover,
                                  }
                                  : undefined,
                              }}
                              onClick={
                                isClickable
                                  ? () => onNotificationClick(notification)
                                  : undefined
                              }
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flex: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                    marginTop: { xs: "4px", sm: 0 },
                                  }}
                                >
                                  <Tooltip
                                    title={
                                      NOTIFICATION_TYPES[notification.type]?.label ||
                                      "Notificación"
                                    }
                                    arrow
                                    placement="bottom"
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      {NotificationIcon === EditNote ? (
                                        <NotificationIcon
                                          sx={{
                                            color: colorConfig.text,
                                            fontSize: isContentNotification
                                              ? 18
                                              : 16,
                                          }}
                                        />
                                      ) : (
                                        <NotificationIcon
                                          size={16}
                                          color={colorConfig.text}
                                        />
                                      )}
                                    </Box>
                                  </Tooltip>
                                </Box>
                                <EllipsisTooltip
                                  text={summaryText}
                                  placement="bottom"
                                  variant="body2"
                                  typographySx={{
                                    flex: 1,
                                    minWidth: 0,
                                    fontWeight: isUnread ? 600 : 500,
                                    color: theme.palette.text.primary,
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                  }}
                                />
                              </Box>
                              {timestamp && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                  }}
                                >
                                  {timestamp}
                                </Typography>
                              )}
                              {isUnread && (
                                <Tooltip title="Marcar como leído" arrow>
                                  <IconButton
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      onMarkRead(notification.id);
                                    }}
                                    sx={{
                                      color: colorConfig.text,
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.action.hover,
                                      },
                                    }}
                                  >
                                    <CheckCheck size={16} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                  )
                ) : (
                  <EmptyStateMessage
                    icon={Bell}
                    message="No hay notificaciones"
                    sx={{ py: 4 }}
                  />
                )}
              </>
            )}
          </Box>
          {showPagination &&
            totalCount > 0 &&
            onPageChange &&
            onRowsPerPageChange && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  borderTop: `1px solid ${theme.palette.divider}`,
                  width: "100%",
                }}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsLegendOpen(true)}
                  sx={{
                    color: theme.palette.primary.main,
                    padding: 0,
                    minWidth: "auto",
                    height: "auto",
                    textDecoration: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                      textDecoration: "underline",
                    },
                  }}
                >
                  Ver leyenda
                </Button>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={onPageChange}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={onRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                  sx={{
                    borderTop: "none",
                    flex: 1,
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    "& .MuiTablePagination-toolbar": {
                      px: { xs: 1, md: 2 },
                      flexWrap: { xs: "wrap", md: "nowrap" },
                      gap: { xs: 1, md: 0 },
                      width: "100%",
                      maxWidth: "100%",
                      overflow: "hidden",
                    },
                    "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                    {
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      whiteSpace: { xs: "nowrap", md: "nowrap" },
                    },
                    "& .MuiTablePagination-select": {
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                    },
                  }}
                />
              </Box>
            )}
        </CardContent>
      </Card>
      <UnifiedModal
        open={isLegendOpen}
        onClose={() => setIsLegendOpen(false)}
        title="Leyenda de notificaciones"
        maxWidth="sm"
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          {notificationLegendItems.map((item) => {
            const LegendIcon = item.icon || Bell;
            return (
              <Box
                key={item.type}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  p: 1,
                  borderRadius: "10px",
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                {LegendIcon === EditNote ? (
                  <LegendIcon
                    sx={{ color: theme.palette.text.primary, fontSize: 18 }}
                  />
                ) : (
                  <LegendIcon size={18} color={theme.palette.text.primary} />
                )}
                <Typography variant="body2">{item.label}</Typography>
              </Box>
            );
          })}
        </Box>
      </UnifiedModal>
    </>
  );
}

export default NotificationPanel;
